import { Fragment, useCallback, useEffect, useState } from 'react';
import { Chip, Grid, Tooltip, TextField } from '@mui/material';
import { makeStyles, TextButton } from '../../../themes';
import { FormikErrors, FormikProps, useFormik } from 'formik';
import { object, string } from 'yup';
import { FormModal } from './FormModal';
import { BasicDialog } from './BasicDialog';
import {
  AutocompleteInput,
  CheckboxInput,
  ChipsInput,
  SelectInput,
  TextInput,
} from '../inputs';
import {
  useDispatch,
  transferInventoryItem,
  getBasicWarehouses,
  useSelector,
  warehousesSelectors,
  getInventoryItemDetails,
  assignTransferInventoryItem,
  getTransferOrderOwnership,
  showError,
} from '../../../state';
import { useGetUsersForSelect, useIsMobile, useRefresh } from '../../../hooks';
import { AuthResponseError } from '../../../lib';
import {
  AddPhysicalItemsToWarehouseLocationRequest,
  InventoryTransferJobRequest,
  OrderOwnershipMoveRequest,
  OrderOwnershipQuantityResponse,
  TransferInventoryRequest,
  WarehouseLocationInventoryItemAPI,
} from '../../../types';
import { MobileModalForm } from '../../../themes/mobile/MobileModalForm';
import { InventoryPhysicalItems } from '../../../pages/inventory/InventoryPhysicalItems';
import { InfoIcon } from '../../../assets';

const INVENTORY_ALLOCATED_KEY = 'InventoryIsAllocated';
const INVENTORY_HAS_ORDER_OWNERSHIP_KEY = 'InventoryHasOrderOwnership';
const INVALID_ORDER_OWNERSHIP_QUANTITY_KEY =
  'InvalidOrderOwnershipQuantity';
const ORDER_OWNED_INVENTORY_REQUIRES_MANUAL_MOVE_KEY =
  'OrderOwnedInventoryRequiresManualMove';

//Surface the allocation-conflict warning in a confirm dialog instead of a toast.
function getAllocatedInventoryWarning(
  error: AuthResponseError | undefined,
): string | null {
  if (error?.code !== INVENTORY_ALLOCATED_KEY) return null;
  return error.message ?? null;
}

function getOrderOwnershipWarning(
  error: AuthResponseError | undefined,
): string | null {
  if (error?.code !== INVENTORY_HAS_ORDER_OWNERSHIP_KEY) return null;
  return error.message ?? null;
}

interface TransferInventoryItemProps {
  open: boolean;
  handleClose: () => any;
  currentWarehouseLocationId: number;
  currentWarehouseLocationName?: string;
  currentWarehouseId: number;
  customerItemId: number;
  customerId: number;
  sku: string;
  moveableUnitId?: string | number | null;
  receiptId?: number;
  receiptIds?: Array<number>;
}

interface RenderProps {
  warehouseId: number | string;
  moveableUnitId?: string | number | null;
  receiptId?: number;
  receiptIds?: Array<number>;
  item: WarehouseLocationInventoryItemAPI | null;
  canEditQty: boolean;
  formik: FormikProps<State>;
  handleShowPhysicalItems: () => void;
}

type RequestType = 'ManualTransfer' | 'AssignedTransfer';

interface State {
  quantity: number | '';
  reason: string;
  receiptId: number | '';
  destinationWarehouseId: number | '';
  destinationWarehouseLocationName: string;
  moveMoveableUnit: boolean;
  requestType: RequestType;
  assigneeUserIds: Array<string | number>;
}

type View = 'details' | 'physicalItems';

interface OrderOwnershipQuantity extends OrderOwnershipQuantityResponse {
  quantityToMove: number;
}

const initialState: State = {
  quantity: '',
  reason: '',
  receiptId: '',
  destinationWarehouseId: '',
  destinationWarehouseLocationName: '',
  moveMoveableUnit: true,
  requestType: 'ManualTransfer',
  assigneeUserIds: [],
};

function getTransferStock(
  item: WarehouseLocationInventoryItemAPI | null,
  values: State,
) {
  if (values.requestType === 'ManualTransfer' && !values.moveMoveableUnit) {
    const receipt = item?.receiptQuantities?.find(
      (r) => r.receiptID === Number(values.receiptId),
    );
    return {
      quantity: item?.receiptQuantities
        ? (receipt?.primaryQuantityRemaining ?? 0)
        : undefined,
      physicalQuantity: receipt?.skuPhysicalItemsQuantity ?? 0,
    };
  }
  return {
    quantity: item?.receiptQuantities
      ? item.receiptQuantities.reduce(
          (sum, r) => sum + r.primaryQuantityRemaining,
          0,
        )
      : item?.totalPrimaryOnHand,
    physicalQuantity: 0,
  };
}

export function TransferInventoryItemForm({
  open,
  handleClose,
  receiptId,
  receiptIds,
  currentWarehouseId,
  currentWarehouseLocationName,
  currentWarehouseLocationId,
  customerItemId,
  customerId,
  sku,
  moveableUnitId,
}: TransferInventoryItemProps) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const refresh = useRefresh();
  const isMobile = useIsMobile();

  const [view, setView] = useState<View>('details');
  const [item, setItem] = useState<WarehouseLocationInventoryItemAPI | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [muPhysicalItemsToTransfer, setMuPhysicalItemsToTransfer] =
    useState<AddPhysicalItemsToWarehouseLocationRequest | null>(null);
  const [allocatedInventoryWarning, setAllocatedInventoryWarning] = useState<
    string | null
  >(null);
  const [pendingForm, setPendingForm] = useState<State | null>(null);
  const [orderOwnershipWarning, setOrderOwnershipWarning] = useState<
    string | null
  >(null);
  const [orderOwnershipQuantities, setOrderOwnershipQuantities] = useState<
    Array<OrderOwnershipQuantity>
  >([]);
  const [pendingOwnershipForm, setPendingOwnershipForm] = useState<{
    values: State;
    force: boolean;
  } | null>(null);

  useEffect(() => {
    dispatch(getBasicWarehouses());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitTransfer = useCallback(
    async (
      values: State,
      force: boolean,
      ownershipQuantities?: Array<OrderOwnershipMoveRequest>,
    ) => {
      const {
        requestType,
        destinationWarehouseId,
        quantity,
        destinationWarehouseLocationName,
        moveMoveableUnit,
        reason,
        receiptId: transferReceiptId,
        assigneeUserIds,
      } = values;
      setSubmitting(true);
      // Suppress the generic error toast so we can surface the allocation warning in a dialog.
      // If a different error comes back we'll show it explicitly.
      if (requestType === 'ManualTransfer') {
        const payload: TransferInventoryRequest = {
          receiptID: transferReceiptId || undefined,
          currentWarehouseLocationID: currentWarehouseLocationId,
          currentWarehouseLocationName,
          currentWarehouseID: currentWarehouseId,
          customerItemID: customerItemId,
          customerID: customerId,
          sku,
          moveableUnitID: moveMoveableUnit ? moveableUnitId : undefined,
          quantity: quantity || undefined,
          reason,
          destinationWarehouseID: destinationWarehouseId || undefined,
          destinationWarehouseLocationName,
          physicalItemsToTransfer: muPhysicalItemsToTransfer,
          forceMoveAllocatedInventory: force || undefined,
          orderOwnershipQuantities: ownershipQuantities,
        };

        const { error } = await dispatch(
          transferInventoryItem(
            payload,
            true,
            /* shouldShowErrorMessage */ false,
          ),
        );
        setSubmitting(false);
        if (!error) {
          setAllocatedInventoryWarning(null);
          setPendingForm(null);
          setOrderOwnershipWarning(null);
          setOrderOwnershipQuantities([]);
          setPendingOwnershipForm(null);
          refresh();
          handleClose();
          return;
        }
        const warning = getAllocatedInventoryWarning(error);
        if (warning && !force) {
          setAllocatedInventoryWarning(warning);
          setPendingForm(values);
          return;
        }
        const ownershipWarning = getOrderOwnershipWarning(error);
        if (ownershipWarning && ownershipQuantities === undefined) {
          const { data, error: ownershipError } = await dispatch(
            getTransferOrderOwnership({
              warehouseLocationID: currentWarehouseLocationId,
              customerItemID: customerItemId,
              receiptID: transferReceiptId || undefined,
              moveableUnitID: moveMoveableUnit ? moveableUnitId : undefined,
            }),
          );
          if (ownershipError || !data?.length) {
            dispatch(
              showError({
                message:
                  ownershipError?.message ||
                  ownershipError?.title ||
                  'Unable to load order-owned inventory',
              }),
            );
            return;
          }
          setOrderOwnershipWarning(ownershipWarning);
          setPendingOwnershipForm({ values, force });
          setOrderOwnershipQuantities(
            data.map((ownership) => ({
              ...ownership,
              quantityToMove: 0,
            })),
          );
          return;
        }
        if (
          error.code === INVALID_ORDER_OWNERSHIP_QUANTITY_KEY &&
          ownershipQuantities !== undefined
        ) {
          dispatch(
            showError({
              message:
                error.message ||
                'Adjust the per-order quantities before moving this inventory.',
            }),
          );
          return;
        }
        // Any other error: close the dialog (if any) and bubble a toast.
        setAllocatedInventoryWarning(null);
        setPendingForm(null);
        setOrderOwnershipWarning(null);
        setOrderOwnershipQuantities([]);
        setPendingOwnershipForm(null);
        dispatch(
          showError({
            message: error.message || error.title || 'Something went wrong',
          }),
        );
      } else if (requestType === 'AssignedTransfer') {
        const payload: InventoryTransferJobRequest = {
          type: 'MoveItem',
          warehouseID: currentWarehouseId,
          customerID: customerId,
          note: reason,
          originLocations: [
            {
              warehouseLocationID: currentWarehouseLocationId,
              warehouseLocationName: currentWarehouseLocationName,
              moveableUnitID: moveMoveableUnit ? moveableUnitId : undefined,
              type: 'Origin',
              taskType: 'MoveInventoryItem',
              totalPrimaryUnitQty: !moveMoveableUnit
                ? quantity || 0
                : undefined,
              customerItemID: customerItemId,
              sku,
            },
          ],
          destinationLocations: [
            {
              warehouseLocationName: destinationWarehouseLocationName,
              type: 'Destination',
              taskType: 'MoveInventoryItem',
              totalPrimaryUnitQty: !moveMoveableUnit
                ? quantity || 0
                : undefined,
              customerItemID: customerItemId,
              sku,
              moveableUnitID: moveMoveableUnit ? moveableUnitId : undefined,
            },
          ],
          assignees: assigneeUserIds.map((u) => ({
            userID: u,
            taskType: 'MoveInventoryItem',
          })),
          forceMoveAllocatedInventory: force || undefined,
        };
        const { error } = await dispatch(
          assignTransferInventoryItem(
            payload,
            true,
            /* shouldShowErrorMessage */ false,
          ),
        );
        setSubmitting(false);
        if (!error) {
          setAllocatedInventoryWarning(null);
          setPendingForm(null);
          setOrderOwnershipWarning(null);
          setOrderOwnershipQuantities([]);
          setPendingOwnershipForm(null);
          refresh();
          handleClose();
          return;
        }
        const warning = getAllocatedInventoryWarning(error);
        if (warning && !force) {
          setAllocatedInventoryWarning(warning);
          setPendingForm(values);
          return;
        }
        setAllocatedInventoryWarning(null);
        setPendingForm(null);
        setOrderOwnershipWarning(null);
        setOrderOwnershipQuantities([]);
        setPendingOwnershipForm(null);
        if (error.code === ORDER_OWNED_INVENTORY_REQUIRES_MANUAL_MOVE_KEY) {
          dispatch(
            showError({
              message:
                'Order-owned inventory must be moved with Manual transfer so you can enter the quantity for each order.',
            }),
          );
          return;
        }
        dispatch(
          showError({
            message: error.message || error.title || 'Something went wrong',
          }),
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentWarehouseId,
      currentWarehouseLocationId,
      currentWarehouseLocationName,
      customerId,
      customerItemId,
      moveableUnitId,
      muPhysicalItemsToTransfer,
      sku,
    ],
  );

  const handleSubmit = useCallback(
    (values: State) => submitTransfer(values, false),
    [submitTransfer],
  );

  const handleConfirmAllocatedMove = useCallback(() => {
    if (pendingForm) {
      submitTransfer(pendingForm, true);
    }
  }, [pendingForm, submitTransfer]);

  const handleCancelAllocatedMove = useCallback(() => {
    // Don't allow dismissal of the dialog while the force-move request is in flight -
    // otherwise they'd perceive a cancel while the destructive request keeps going.
    if (submitting) return;
    setAllocatedInventoryWarning(null);
    setPendingForm(null);
  }, [submitting]);

  const handleConfirmOwnershipChoice = useCallback(() => {
    if (pendingOwnershipForm) {
      submitTransfer(
        pendingOwnershipForm.values,
        pendingOwnershipForm.force,
        orderOwnershipQuantities
          .filter((ownership) => ownership.quantityToMove > 0)
          .map(({ orderID, quantityToMove }) => ({
            orderID,
            quantity: quantityToMove,
          })),
      );
    }
  }, [pendingOwnershipForm, orderOwnershipQuantities, submitTransfer]);

  const handleCancelOwnershipChoice = useCallback(() => {
    if (submitting) return;
    setOrderOwnershipWarning(null);
    setPendingOwnershipForm(null);
    setOrderOwnershipQuantities([]);
  }, [submitting]);

  const handleOrderOwnershipQuantityChange = useCallback(
    (orderID: number, value: string) => {
      const quantityToMove = value === '' ? 0 : Number(value);
      setOrderOwnershipQuantities((current) =>
        current.map((ownership) =>
          ownership.orderID === orderID
            ? { ...ownership, quantityToMove }
            : ownership,
        ),
      );
    },
    [],
  );

  const selectedOrderOwnershipQuantity = orderOwnershipQuantities.reduce(
    (total, ownership) => total + ownership.quantityToMove,
    0,
  );
  const totalOrderOwnershipQuantity = orderOwnershipQuantities.reduce(
    (total, ownership) => total + ownership.quantity,
    0,
  );
  const requestedMoveQuantity = Number(
    pendingOwnershipForm?.values.quantity || 0,
  );
  const totalInventoryQuantity =
    orderOwnershipQuantities[0]?.totalInventoryQuantity ??
    item?.primaryQuantityRemaining;
  const minimumOrderOwnershipQuantityToMove = totalInventoryQuantity
    ? Math.max(
        0,
        totalOrderOwnershipQuantity -
          Math.max(
            0,
            totalInventoryQuantity - requestedMoveQuantity,
          ),
      )
    : 0;

  const isOrderOwnershipChoiceValid =
    !!pendingOwnershipForm &&
    orderOwnershipQuantities.every(
      ({ quantity, quantityToMove }) =>
        Number.isInteger(quantityToMove) &&
        quantityToMove >= 0 &&
        quantityToMove <= quantity,
    ) &&
    selectedOrderOwnershipQuantity <= requestedMoveQuantity &&
    selectedOrderOwnershipQuantity >= minimumOrderOwnershipQuantityToMove;

  const formik = useFormik<State>({
    initialValues: {
      ...initialState,
      destinationWarehouseId: currentWarehouseId || '',
      receiptId: receiptId || '',
      moveMoveableUnit: !!moveableUnitId,
    },
    enableReinitialize: false,
    onSubmit: handleSubmit,
    validationSchema: schema,
    validate: (values) => {
      const errors: FormikErrors<State> = {};
      if (!values.moveMoveableUnit) {
        if (values.requestType === 'ManualTransfer' && !values.receiptId) {
          errors.receiptId = 'Select a receipt';
        }
        const { quantity: onHand } = getTransferStock(item, values);
        if (
          !Number.isInteger(Number(values.quantity)) ||
          Number(values.quantity) <= 0
        ) {
          errors.quantity = 'Enter a positive whole number';
        } else if (
          (values.requestType !== 'ManualTransfer' || !!values.receiptId) &&
          onHand !== undefined &&
          Number(values.quantity) > onHand
        ) {
          errors.quantity = `Only ${onHand} primary units are on hand${values.requestType === 'ManualTransfer' ? ' for this receipt' : ''}`;
        }
      }
      return errors;
    },
  });

  useEffect(() => {
    if (!open) return;
    let active = true;
    setItem(null);
    (async () => {
      const { data } = await dispatch(
        getInventoryItemDetails(currentWarehouseLocationId, customerItemId, {
          // Quantity moves operate across MUs at the location, while full-MU moves
          // retain the selected MU's scope. Fetch every receipt for the dropdown.
          moveableUnitID:
            formik.values.moveMoveableUnit && moveableUnitId
              ? moveableUnitId
              : undefined,
          warehouseID: currentWarehouseId,
          includeReceiptQuantities: true,
        }),
      );
      if (active) setItem(data ?? null);
    })();
    return () => {
      active = false;
    };
  }, [
    open,
    dispatch,
    currentWarehouseLocationId,
    customerItemId,
    currentWarehouseId,
    moveableUnitId,
    formik.values.moveMoveableUnit,
  ]);

  useEffect(() => {
    // Tracking selections belong to a specific receipt and move quantity.
    setMuPhysicalItemsToTransfer(null);
    setView('details');
  }, [
    formik.values.receiptId,
    formik.values.requestType,
    formik.values.moveMoveableUnit,
    formik.values.quantity,
  ]);

  const handleShowDetails = useCallback(() => {
    setView('details');
  }, []);

  const handleShowPhysicalItems = useCallback(() => {
    setView('physicalItems');
  }, []);

  const handleGetPhysicalItemsToTransfer = useCallback(
    (payload: AddPhysicalItemsToWarehouseLocationRequest) => {
      setMuPhysicalItemsToTransfer(payload);
      setView('details');
    },
    [],
  );

  const canEditQty = !formik.values.moveMoveableUnit;

  if (isMobile) {
    return (
      <MobileModalForm
        open={open}
        handleClose={handleClose}
        callback={formik.handleSubmit}
        disableSubmit={submitting || !item}
        btnText='Transfer'
        submitting={submitting}
        title='Move item'
      >
        {view === 'physicalItems' &&
        currentWarehouseLocationId &&
        customerItemId &&
        formik.values.receiptId ? (
          <InventoryPhysicalItems
            mode='transfer'
            receiptId={formik.values.receiptId}
            warehouseLocationId={currentWarehouseLocationId}
            moveableUnitId={
              formik.values.moveMoveableUnit ? moveableUnitId : undefined
            }
            customerItemId={customerItemId}
            sku={sku}
            handleBack={handleShowDetails}
            canEdit
            updatedSkuQuantity={formik.values.quantity}
            handleSubmitOverride={handleGetPhysicalItemsToTransfer}
          />
        ) : (
          <RenderTransferForm
            formik={formik}
            warehouseId={currentWarehouseId}
            moveableUnitId={moveableUnitId}
            receiptIds={receiptIds}
            receiptId={receiptId}
            item={item}
            canEditQty={canEditQty}
            handleShowPhysicalItems={handleShowPhysicalItems}
          />
        )}
        {!!allocatedInventoryWarning && (
          <BasicDialog
            open
            title='Inventory is allocated'
            text={allocatedInventoryWarning}
            btnText='Move anyway'
            cancelBtnText='Cancel'
            handleClose={handleCancelAllocatedMove}
            callback={handleConfirmAllocatedMove}
            submitting={submitting}
            disableCallback={submitting}
          />
        )}
        {!!orderOwnershipWarning && (
          <BasicDialog
            open
            title='Order-owned inventory'
            btnText='Move selected quantities'
            cancelBtnText='Cancel'
            handleClose={handleCancelOwnershipChoice}
            callback={handleConfirmOwnershipChoice}
            submitting={submitting}
            disableCallback={submitting || !isOrderOwnershipChoiceValid}
          >
            <p>{orderOwnershipWarning}</p>
            <p>Enter how much of each order is included in this move.</p>
            {minimumOrderOwnershipQuantityToMove > 0 && (
              <p>{`Select at least ${minimumOrderOwnershipQuantityToMove} order-owned unit(s) so every claim left at this location remains covered by inventory.`}</p>
            )}
            {orderOwnershipQuantities.map((ownership) => (
              <div
                key={ownership.orderID}
                className={classes.orderOwnershipRow}
              >
                <span>{`Order #${ownership.orderID} (up to ${ownership.quantity})`}</span>
                <TextField
                  size='small'
                  type='number'
                  value={ownership.quantityToMove}
                  slotProps={{
                    htmlInput: { min: 0, max: ownership.quantity, step: 1 },
                  }}
                  onChange={(event) =>
                    handleOrderOwnershipQuantityChange(
                      ownership.orderID,
                      event.target.value,
                    )
                  }
                />
              </div>
            ))}
          </BasicDialog>
        )}
      </MobileModalForm>
    );
  }

  return (
    <FormModal
      open={open}
      handleClose={handleClose}
      title={<span className={classes.title}>Move item</span>}
      callback={formik.handleSubmit}
      btnText='TRANSFER'
      typeSubmit
      maxWidth='md'
      paperProps={{ style: { width: 720 } }}
      submitting={submitting}
      disableSubmit={!item}
      hasCustomActionBtn={view === 'physicalItems'}
    >
      {view === 'physicalItems' &&
      currentWarehouseLocationId &&
      customerItemId &&
      formik.values.receiptId ? (
        <InventoryPhysicalItems
          mode='transfer'
          receiptId={formik.values.receiptId}
          warehouseLocationId={currentWarehouseLocationId}
          moveableUnitId={
            formik.values.moveMoveableUnit ? moveableUnitId : undefined
          }
          customerItemId={customerItemId}
          sku={sku}
          handleBack={handleShowDetails}
          canEdit
          handleSubmitOverride={handleGetPhysicalItemsToTransfer}
          updatedSkuQuantity={formik.values.quantity}
        />
      ) : (
        <Fragment>
          <div className={classes.locationContainer}>
            {currentWarehouseLocationName && (
              <div>{currentWarehouseLocationName}</div>
            )}
            {sku && currentWarehouseLocationName && (
              <span className={classes.dot} />
            )}
            {sku && <div className={classes.sku}>{sku}</div>}
          </div>
          <RenderTransferForm
            formik={formik}
            warehouseId={currentWarehouseId}
            moveableUnitId={moveableUnitId}
            receiptIds={receiptIds}
            receiptId={receiptId}
            item={item}
            canEditQty={canEditQty}
            handleShowPhysicalItems={handleShowPhysicalItems}
          />
        </Fragment>
      )}
      {!!allocatedInventoryWarning && (
        <BasicDialog
          open
          title='Inventory is allocated'
          text={allocatedInventoryWarning}
          btnText='Move anyway'
          cancelBtnText='Cancel'
          handleClose={handleCancelAllocatedMove}
          callback={handleConfirmAllocatedMove}
          submitting={submitting}
          disableCallback={submitting}
        />
      )}
      {!!orderOwnershipWarning && (
        <BasicDialog
          open
          title='Order-owned inventory'
          btnText='Move selected quantities'
          cancelBtnText='Cancel'
          handleClose={handleCancelOwnershipChoice}
          callback={handleConfirmOwnershipChoice}
          submitting={submitting}
          disableCallback={submitting || !isOrderOwnershipChoiceValid}
        >
          <p>{orderOwnershipWarning}</p>
          <p>Enter how much of each order is included in this move.</p>
          {minimumOrderOwnershipQuantityToMove > 0 && (
            <p>{`Select at least ${minimumOrderOwnershipQuantityToMove} order-owned unit(s) so every claim left at this location remains covered by inventory.`}</p>
          )}
          {orderOwnershipQuantities.map((ownership) => (
            <div key={ownership.orderID} className={classes.orderOwnershipRow}>
              <span>{`Order #${ownership.orderID} (up to ${ownership.quantity})`}</span>
              <TextField
                size='small'
                type='number'
                value={ownership.quantityToMove}
                slotProps={{
                  htmlInput: { min: 0, max: ownership.quantity, step: 1 },
                }}
                onChange={(event) =>
                  handleOrderOwnershipQuantityChange(
                    ownership.orderID,
                    event.target.value,
                  )
                }
              />
            </div>
          ))}
        </BasicDialog>
      )}
    </FormModal>
  );
}

function RenderTransferForm({
  formik,
  warehouseId,
  moveableUnitId,
  receiptIds,
  receiptId,
  item,
  canEditQty,
  handleShowPhysicalItems,
}: RenderProps) {
  const { classes, css } = useStyles();
  const isMobile = useIsMobile();

  const warehouses = useSelector(warehousesSelectors.basicList);

  const [users, usersObj] = useGetUsersForSelect({
    warehouseId,
    userTypes: ['Scanner', 'ScannerProcessor'],
  });

  const { quantity: skuQuantity, physicalQuantity: skuPhysicalItemsQuantity } =
    getTransferStock(item, formik.values);
  const receiptOptions = Array.from(
    new Set([
      ...(receiptIds ?? []),
      ...(item?.receiptQuantities?.map((r) => r.receiptID) ?? []),
    ]),
  ).map((id) => {
    const quantity = item?.receiptQuantities?.find(
      (r) => r.receiptID === id,
    )?.primaryQuantityRemaining;
    return {
      id,
      name: `Receipt ${id} — ${item?.receiptQuantities ? `${quantity ?? 0} primary units on hand` : 'quantity unavailable'}`,
    };
  });

  // This is the maximum quantity that can be removed without needing to remove physical items
  // When removing more than this amount, physical items must also be removed
  const maxQtyAllowedToRemove = (skuQuantity ?? 0) - skuPhysicalItemsQuantity;

  return (
    <div>
      <div className={classes.subTitle}>
        Specify the qty and reason for moving these items. Then enter a new
        location to move it to.
      </div>
      <div className={classes.chips}>
        <ChipsInput
          formikProps={formik}
          label='How do you want to move the item?'
          name='requestType'
          chips={[
            {
              label: 'Manual transfer',
              value: 'ManualTransfer' as RequestType,
            },
            {
              label: 'Assign users',
              value: 'AssignedTransfer' as RequestType,
            },
          ]}
        />
      </div>
      <Grid container spacing={3} rowSpacing={2}>
        {!!moveableUnitId && (
          <Grid
            size={{
              sm: 12,
            }}
          >
            <CheckboxInput
              formikProps={formik}
              checkboxes={[
                {
                  labelProps: {
                    label: `Move with MU ${moveableUnitId}`,
                  },
                  checkboxProps: {
                    name: 'moveMoveableUnit',
                  },
                },
              ]}
            />
            {/* <RadioInput
              formikProps={formik}
              name='moveMoveableUnit'
              radiosRow
              title={`Move with MU ${moveableUnitId}`}
              size='small'
              radios={[
                {
                  label: 'Yes',
                  value: 'yes' as ShouldMoveType,
                },
                { label: 'No', value: 'no' as ShouldMoveType },
              ]}
            /> */}
          </Grid>
        )}
        {canEditQty && (
          <Grid
            size={{
              sm: isMobile ? 12 : 3,
            }}
          >
            <TextInput
              name='quantity'
              formikProps={formik}
              label='Primary QTY to move'
              helperText={
                formik.touched.quantity && formik.errors.quantity
                  ? formik.errors.quantity
                  : formik.values.requestType === 'ManualTransfer' &&
                      !formik.values.receiptId
                    ? 'Select a receipt to see its quantity'
                    : skuQuantity === undefined
                      ? 'On-hand quantity unavailable'
                      : `${skuQuantity} primary units on hand`
              }
              className={classes.input}
              type='number'
              slotProps={{
                htmlInput: {
                  max: skuQuantity,
                  min: 1,
                  step: 1,
                },
                input: {
                  endAdornment:
                    skuPhysicalItemsQuantity > 0 ? (
                      <Tooltip
                        title={
                          maxQtyAllowedToRemove <= 0
                            ? `You cannot reduce the quantity without also removing physical items. There are currently ${skuPhysicalItemsQuantity} physical items for this SKU, so you must remove these physical items first in the inventory tracking screen before reducing the quantity.`
                            : `You can reduce the quantity by up to ${maxQtyAllowedToRemove} without removing physical items. If you need to reduce by more than that, you must also remove physical items from the inventory tracking screen. The new quantity cannot be less than the physical item count (${skuPhysicalItemsQuantity}).`
                        }
                      >
                        <InfoIcon
                          className={css({
                            fontSize: 16,
                            color: '#A5AABD',
                            marginLeft: 8,
                          })}
                        />
                      </Tooltip>
                    ) : undefined,
                },
              }}
            />
          </Grid>
        )}
        <Grid
          size={{
            sm: isMobile || !canEditQty ? 12 : 9,
          }}
        >
          <TextInput
            name='reason'
            formikProps={formik}
            label='Reason'
            className={classes.input}
          />
        </Grid>
        {!receiptId &&
          canEditQty &&
          formik.values.requestType === 'ManualTransfer' && (
            <Grid
              size={{
                sm: 12,
              }}
            >
              <SelectInput
                name='receiptId'
                formikProps={formik}
                label='Select a receipt'
                options={receiptOptions}
                disabled={!item}
                className={classes.input}
              />
            </Grid>
          )}
      </Grid>
      {canEditQty && !!item?.unavailableQuantity && (
        <p>
          {item.unavailableQuantity} primary units across this location are
          unavailable due to allocations, holds, or other commitments. Receipt
          quantities show on-hand stock; movement restrictions are checked when
          you transfer.
        </p>
      )}
      {formik.values.requestType === 'ManualTransfer' &&
        canEditQty &&
        skuPhysicalItemsQuantity > 0 &&
        formik.values.quantity !== '' && (
          <div>
            <TextButton onClick={handleShowPhysicalItems}>
              Transfer physical items
            </TextButton>
          </div>
        )}
      <div className={classes.sectionTitle}>Select a new location</div>
      <Grid container spacing={5} rowSpacing={2}>
        <Grid
          size={{
            sm: isMobile ? 12 : 6,
          }}
        >
          <SelectInput
            name='destinationWarehouseId'
            formikProps={formik}
            label='Select a warehouse'
            options={warehouses}
            className={classes.input}
          />
        </Grid>
        <Grid
          size={{
            sm: isMobile ? 12 : 6,
          }}
        >
          <TextInput
            name='destinationWarehouseLocationName'
            formikProps={formik}
            label='Enter a location'
            className={classes.input}
          />
        </Grid>
      </Grid>
      {formik.values.requestType === 'AssignedTransfer' && (
        <div>
          <div className={classes.sectionTitle}>Assign users</div>
          <AutocompleteInput
            formikProps={formik}
            textFieldProps={{
              name: 'assigneeUserIds',
              label: 'Assign users',
              required: true,
            }}
            autocompleteProps={{
              // className: classes.smallInput,
              options: users,
              multiple: true,
              renderTags: (tagValue, getTagProps) =>
                tagValue.map((option, index) => {
                  const { key, ...otherProps } = getTagProps({ index });
                  const optionId =
                    typeof option === 'object' && option !== null
                      ? option.id
                      : option;
                  return (
                    <Chip
                      key={key}
                      label={usersObj[optionId]}
                      {...otherProps}
                    />
                  );
                }),
            }}
          />
        </div>
      )}
    </div>
  );
}

const useStyles = makeStyles({ name: { TransferInventoryItemForm } })(
  (theme) => ({
    title: {
      fontFamily: 'Montserrat',
      fontSize: 20,
      fontWeight: 600,
    },
    subTitle: {
      fontFamily: 'Montserrat',
      fontSize: 14,
      letterSpacing: 0,
      marginBottom: 16,
    },
    input: {
      width: '100%',
    },
    sectionTitle: {
      fontFamily: 'Montserrat',
      fontSize: 14,
      fontWeight: 'bold',
      letterSpacing: 0,
      marginTop: 40,
      marginBottom: 16,
      [theme.breakpoints.down('tablet')]: {
        marginTop: 16,
      },
    },
    sku: {
      color: '#00C4F8',
      fontFamily: 'Montserrat',
      fontSize: 14,
      fontWeight: 600,
      letterSpacing: 0,
    },
    locationContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    dot: {
      height: 8,
      width: 8,
      marginRight: 8,
      borderRadius: 10,
      background: '#000000',
      margin: '0px 8px',
    },
    chips: {
      width: 400,
    },
    orderOwnershipRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 16,
      marginTop: 12,
    },
  }),
);

const schema = object().shape({
  destinationWarehouseId: string().required('Required'),
  destinationWarehouseLocationName: string().required('Required'),
  reason: string().required('Required'),
});
